var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { CorrelationVector, Log, Operation as LoggingOperation, LogCategory } from '@augloop/logging';
import { Utils } from '@augloop/runtime-client';
var Request = /** @class */ (function () {
    function Request(requestId, correlationId, schemaName, data, callback, requestTimeoutInMs) {
        this.requestId = requestId;
        this.correlationId = correlationId;
        this.schemaName = schemaName;
        this.data = data;
        this.callback = callback;
        this.requestTimeoutInMs = requestTimeoutInMs;
        this.ended = false;
        this.startTime = 0;
    }
    Request.prototype.startRequest = function () {
        this.startTime = Utils.getCurrentTimeMs();
    };
    Request.prototype.getMsUntilTimeout = function () {
        var elapsed = Utils.getCurrentTimeMs() - this.startTime;
        return this.requestTimeoutInMs - elapsed;
    };
    Request.prototype.endRequest = function (error) {
        if (!this.ended) {
            this.invokeCallback(null, error, true);
            this.ended = true;
        }
    };
    Request.prototype.addResponseData = function (data) {
        this.invokeCallback(data, null, false);
    };
    Request.prototype.invokeCallback = function (data, error, end) {
        var _this = this;
        // Create a promise to invoke the callback and ignore error to ensure that the current
        // thread won't be affected if callback throws.
        new Promise(function () { return _this.callback(data, error, end); }).catch(function (_reason) { });
    };
    return Request;
}());
var WebSocketRequestManager = /** @class */ (function () {
    function WebSocketRequestManager(requestTimeoutInMs, workerFactory, maxActiveRequests) {
        this.requestCounter = 0;
        this.activeRequests = [];
        this.pendingRequests = [];
        this.requestTimeoutInMs = requestTimeoutInMs;
        this.workerFactory = workerFactory;
        this.maxActiveRequests = maxActiveRequests;
    }
    WebSocketRequestManager.prototype.testConnection = function (url, clientMetadata) {
        var _this = this;
        var receivedResponse = false;
        var logOp = new LoggingOperation({
            cv: (new CorrelationVector()).toString(),
            operationName: 'WebSocketTest',
            resourceId: url,
            success: true,
        }).start();
        return new Promise(function (resolve) {
            var healthCheckRequest = Utils.createHealthCheckRequest(clientMetadata);
            if (!url) {
                logOp.success = false;
                logOp.resultSignature = 'EmptyUrl';
                Log.info(0x213cf08e /* tag_7ppco */, LogCategory.CoreDefault, logOp.stop());
                resolve(logOp.success);
            }
            else {
                _this.addRequest(url, healthCheckRequest, logOp.cv, 'HealthCheckRequest', function (data, error, end) {
                    if (error) {
                        logOp.success = false;
                        logOp.resultSignature = 'Error';
                        logOp.resultDescription = error.message || error;
                    }
                    if (data && data.status === 'OK') {
                        receivedResponse = true;
                    }
                    if (end) {
                        if (logOp.success && !receivedResponse) {
                            logOp.success = false;
                            logOp.resultSignature = 'NoResponseData';
                        }
                        Log.info(0x212d50dd /* tag_7lvd3 */, LogCategory.CoreDefault, logOp.stop());
                        resolve(logOp.success);
                    }
                });
            }
        });
    };
    WebSocketRequestManager.prototype.addRequest = function (url, data, correlationId, schemaName, callback) {
        var requestId = (this.requestCounter++).toString();
        var request = new Request(requestId, correlationId, schemaName, data, callback, this.requestTimeoutInMs);
        this.getWorker(url);
        this.pendingRequests.push(request);
        this.egressPendingRequests();
    };
    WebSocketRequestManager.prototype.egressPendingRequests = function () {
        while (this.pendingRequests.length > 0 && this.activeRequests.length < this.maxActiveRequests) {
            var request = this.pendingRequests.shift();
            this.activeRequests.push(request);
            if (this.activeRequests.length === 1) {
                var timeout = request.getMsUntilTimeout();
                setTimeout(this.onTimeout.bind(this), timeout);
            }
            request.startRequest();
            if (this.worker) {
                this.worker.egress(this.createRequestMessage(request.data, request.requestId, request.correlationId));
            }
        }
    };
    WebSocketRequestManager.prototype.createRequestMessage = function (data, requestId, correlationId) {
        return JSON.stringify({ correlationId: correlationId, requestId: requestId, body: data });
    };
    WebSocketRequestManager.prototype.onTimeout = function () {
        // Requests in the queue are checked in order to see if they should be ended.
        while (this.activeRequests.length > 0) {
            var request = this.activeRequests[0];
            var timeout = request.getMsUntilTimeout();
            if (timeout <= 0) {
                // A timed out request will be ended and removed from the queue.
                var errorTimeOut = new Error("Request timed out");
                request.endRequest(errorTimeOut);
                this.activeRequests.shift();
                this.egressPendingRequests();
            }
            else {
                // If the top request is not timed out yet, reset the timer to its timeout.
                setTimeout(this.onTimeout.bind(this), timeout);
                return;
            }
        }
    };
    WebSocketRequestManager.prototype.onMessage = function (message) {
        try {
            var parsedData = JSON.parse(message);
            for (var i = 0; i < this.activeRequests.length; i++) {
                var request = this.activeRequests[i];
                if (request.requestId === parsedData.requestId) {
                    if (parsedData.end) {
                        this.activeRequests.splice(i, 1);
                        request.endRequest(parsedData.error);
                        this.egressPendingRequests();
                    }
                    else if (parsedData.body) {
                        request.addResponseData(parsedData.body);
                    }
                    break;
                }
            }
        }
        catch (err) {
            Log.error(0x213cf08f /* tag_7ppcp */, LogCategory.CoreDefault, err);
        }
    };
    WebSocketRequestManager.prototype.onClose = function (error) {
        var e_1, _a, e_2, _b;
        var errorClosedConnection = new Error("Connection closed: ".concat(error));
        try {
            for (var _c = __values(this.activeRequests), _d = _c.next(); !_d.done; _d = _c.next()) {
                var request = _d.value;
                request.endRequest(errorClosedConnection);
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
            }
            finally { if (e_1) throw e_1.error; }
        }
        this.activeRequests = [];
        try {
            for (var _e = __values(this.pendingRequests), _f = _e.next(); !_f.done; _f = _e.next()) {
                var request = _f.value;
                request.endRequest(errorClosedConnection);
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (_f && !_f.done && (_b = _e.return)) _b.call(_e);
            }
            finally { if (e_2) throw e_2.error; }
        }
        this.pendingRequests = [];
        this.worker = undefined;
    };
    WebSocketRequestManager.prototype.getWorker = function (url) {
        // Create a worker on that URL if a worker doesn't exist yet.
        // Note: the assumption here is that the URL is always the same.
        if (!this.worker) {
            this.worker = this.workerFactory();
            this.worker.init(url, this.onMessage.bind(this), function () { }, this.onClose.bind(this));
        }
        return this.worker;
    };
    return WebSocketRequestManager;
}());
export { WebSocketRequestManager };
//# sourceMappingURL=websocket-request-manager.js.map